import { forwardRef } from 'react';
import type { PropsWithoutRef } from 'react';
import styled from 'styled-components';

import type { NoChildrenProp, RefElement, WithAttributes } from '../../types';
import { defaultThemeProp } from '../../theme';
import { useTheme } from '../../hooks';
import Button from '../Button';
import Flex from '../Flex';
import Icon, { registerIcon } from '../Icon';
import * as polarisSolidIcon from '../Icon/icons/polaris-solid.icon';
import { ThemeOverride } from '../Configuration';

registerIcon(polarisSolidIcon);

export type AIButtonProps = WithAttributes<
  'button',
  NoChildrenProp & {
    /**
     * Controls the styling of the Button.
     * @default 'secondary'
     */
    variant?: 'primary' | 'secondary';
    /** The text content of the AIButton. */
    label: string;
  } & (
      | {
          /**
           * Icon name. Set to null to render button without an icon.
           * @default 'polaris-solid'
           */
          icon?: string;
          /**
           * Only display the icon. The label prop will be forwarded to a tooltip.
           * @default false
           */
          iconOnly: true;
        }
      | {
          /**
           * Icon name. Set to null to render button without an icon.
           * @default 'polaris-solid'
           */
          icon?: string | null;
          /**
           * Only display the icon. The label prop will be forwarded to a tooltip.
           * @default false
           */
          iconOnly?: false;
        }
    )
>;

export const StyledAIButton = styled(Button)``;

StyledAIButton.defaultProps = defaultThemeProp;

export default forwardRef<RefElement<AIButtonProps>, PropsWithoutRef<AIButtonProps>>(
  function AIButton(
    {
      label,
      icon = 'polaris-solid',
      disabled = false,
      iconOnly,
      variant = 'secondary',
      ...restProps
    },
    ref
  ) {
    const theme = useTheme();
    return (
      <ThemeOverride
        theme={{
          components: {
            button: {
              color: theme.base.palette.ai,
              'secondary-color': theme.base.palette.ai,
              'secondary-fill-style': 'outline'
            }
          }
        }}
      >
        <StyledAIButton
          {...restProps}
          variant={variant}
          ref={ref}
          disabled={disabled}
          icon={iconOnly}
          label={iconOnly ? label : undefined}
        >
          <Flex container={{ alignItems: 'center', gap: 1 }} as='span'>
            {icon && <Icon name={icon} />}
            {!iconOnly && <span>{label}</span>}
          </Flex>
        </StyledAIButton>
      </ThemeOverride>
    );
  }
);
